import { useEffect, useState } from 'react';
import { useMutationState, useQuery } from '@tanstack/react-query';

import { api } from '../api';
import type { Me } from '../App';
import { calendarPushStatus, type Calendar, type Account } from '../types';

type SyncResult = {
  ok: boolean;
  synced: number;
  errors: { account_id: string; message: string }[];
};

const SUCCESS_HOLD_MS = 3000;

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 5) return 'just now';
  if (s < 45) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function Chip({
  tone,
  children,
  title
}: {
  tone: 'muted' | 'bad';
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      className="flex-shrink-0 text-[0.7rem] uppercase tracking-wide"
      style={{ color: tone === 'bad' ? 'var(--today-fg)' : 'var(--text-faint)' }}
    >
      {children}
    </span>
  );
}

function Row({
  label,
  chip,
  subtitle,
  detail
}: {
  label: string;
  chip: React.ReactNode;
  subtitle?: React.ReactNode;
  detail?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex flex-col gap-[2px] last:mb-0">
      <div className="flex items-center justify-between gap-2">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[var(--text)]">
          {label}
        </span>
        {chip}
      </div>
      {subtitle && (
        <div className="text-[0.75rem] text-[var(--text-muted)]">{subtitle}</div>
      )}
      {detail && (
        <div
          className="mt-1 break-words rounded px-2 py-1 text-[0.72rem]"
          style={{ background: 'var(--today-bg)', color: 'var(--today-fg)' }}
        >
          {detail}
        </div>
      )}
    </div>
  );
}

export default function SyncStatusIndicator() {
  const q = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
    refetchInterval: 30_000
  });
  const calsQ = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.get<Calendar[]>('/api/calendars')
  });
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/me')
  });
  const pushConfigured = !!meQ.data?.push_configured;
  const cals = calsQ.data ?? [];
  const degradedCount = cals.filter(
    (c) => calendarPushStatus(c, pushConfigured) === 'degraded'
  ).length;

  const syncStates = useMutationState({
    filters: { mutationKey: ['sync'] },
    select: (m) => ({
      status: m.state.status,
      submittedAt: m.state.submittedAt,
      data: m.state.data as SyncResult | undefined,
      error: m.state.error as Error | null
    })
  });
  const latest = syncStates[syncStates.length - 1];
  const isPending = latest?.status === 'pending';

  const [holdOpen, setHoldOpen] = useState(false);
  useEffect(() => {
    if (!latest) return;
    if (latest.status !== 'success' && latest.status !== 'error') return;
    setHoldOpen(true);
    const t = window.setTimeout(() => setHoldOpen(false), SUCCESS_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [latest?.status, latest?.submittedAt]);

  const [locked, setLocked] = useState(false);

  const accounts = q.data ?? [];
  const accountError = accounts.some((a) => a.status === 'error');
  const syncCallFailed = latest?.status === 'error';
  const syncReturnedErrors = latest?.status === 'success' && !!latest.data && !latest.data.ok;
  const serverUnreachable = q.isError && !q.isFetching;
  const pushDegraded = pushConfigured && degradedCount > 0;
  const hasError =
    serverUnreachable ||
    accountError ||
    syncCallFailed ||
    syncReturnedErrors ||
    pushDegraded;

  // Dot: pulse on accent while syncing, red when something's wrong,
  // otherwise faint. No standalone "everything's fine" green — silence
  // is the success state.
  let dotColor = 'var(--text-faint)';
  if (isPending) dotColor = 'var(--accent)';
  else if (hasError) dotColor = 'var(--today-fg)';

  const banner = (() => {
    if (serverUnreachable) {
      return { text: 'Reconnecting…', tone: 'bad' as const };
    }
    if (isPending) return { text: 'Syncing…', tone: 'muted' as const };
    if (syncCallFailed) {
      return { text: latest?.error?.message ?? 'Sync failed', tone: 'bad' as const };
    }
    if (latest?.status === 'success' && holdOpen) {
      const data = latest.data;
      if (data?.ok) {
        const n = data.synced;
        return {
          text: `Synced ${n} ${n === 1 ? 'account' : 'accounts'}`,
          tone: 'muted' as const
        };
      }
      if (data) {
        const n = data.errors.length;
        return {
          text: `Synced ${data.synced} ${data.synced === 1 ? 'account' : 'accounts'} · ${n} failed`,
          tone: 'bad' as const
        };
      }
    }
    return null;
  })();

  const forceShow = isPending || holdOpen || locked || serverUnreachable;

  const pushRow = (() => {
    if (!meQ.data) return null;
    if (!pushConfigured) {
      return (
        <Row
          label="Push"
          chip={<Chip tone="muted" title="Server has no public origin configured.">Disabled</Chip>}
          subtitle="Polling only"
        />
      );
    }
    if (pushDegraded) {
      return (
        <Row
          label="Push"
          chip={
            <Chip tone="bad" title="Watch channels stale or failed to register.">
              Degraded
            </Chip>
          }
          subtitle={`${degradedCount} calendar${degradedCount === 1 ? '' : 's'} on slow poll`}
        />
      );
    }
    return <Row label="Push" chip={<Chip tone="muted">Active</Chip>} />;
  })();

  return (
    <div className="group fixed bottom-3 right-3 z-40">
      <div
        className={
          'absolute bottom-full right-0 mb-2 w-[320px] rounded-md border border-[var(--border)] bg-[var(--popover-bg)] p-3 text-sm shadow-[var(--shadow-md)] group-hover:block ' +
          (locked ? 'pointer-events-auto ' : 'pointer-events-none ') +
          (forceShow ? 'block' : 'hidden')
        }
        role="tooltip"
      >
        <div className="mb-2 text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
          Status
        </div>

        {banner && (
          <div
            className="mb-3 rounded px-2 py-[6px] text-[0.78rem]"
            style={
              banner.tone === 'bad'
                ? { background: 'var(--today-bg)', color: 'var(--today-fg)' }
                : { background: 'var(--bg-muted)', color: 'var(--text-muted)' }
            }
          >
            {banner.text}
          </div>
        )}

        {pushRow}

        {accounts.length === 0 && !banner && (
          <div className="text-[var(--text-muted)]">No accounts connected.</div>
        )}
        {accounts.map((a) => {
          const errored = a.status === 'error';
          return (
            <Row
              key={a.id}
              label={a.source_account_id}
              chip={
                errored ? (
                  <Chip tone="bad">Error</Chip>
                ) : (
                  <Chip tone="muted">{a.status}</Chip>
                )
              }
              subtitle={`Synced ${timeAgo(a.last_synced_at)}`}
              detail={errored ? a.error_message || a.status : undefined}
            />
          );
        })}
      </div>
      <button
        type="button"
        aria-label={hasError ? 'Sync error' : isPending ? 'Syncing' : 'Sync ok'}
        aria-pressed={locked}
        onClick={() => setLocked((v) => !v)}
        className={
          'grid h-5 w-5 place-items-center rounded-full border bg-[var(--bg)] shadow-[var(--shadow-sm)] hover:border-[var(--border-strong)] ' +
          (locked ? 'border-[var(--accent)]' : 'border-[var(--border)]')
        }
      >
        <span
          aria-hidden
          className={'block h-2 w-2 rounded-full ' + (isPending ? 'animate-pulse' : '')}
          style={{ background: dotColor }}
        />
      </button>
    </div>
  );
}
